import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, combineLatest, of } from 'rxjs';
import { map, catchError, tap } from 'rxjs/operators';
import { CalendarEvent, CalendarState, EventFilters, EventStatus, EventType } from '../models/calendar-event.interface';
import { CalendarRepositoryService } from './calendar-repository.service';

@Injectable({
  providedIn: 'root'
})
export class CalendarService {
  private stateSubject = new BehaviorSubject<CalendarState>({
    events: [],
    selectedEvent: null,
    viewMode: 'timeline',
    filters: {},
    isLoading: false,
    error: null
  });

  public state$ = this.stateSubject.asObservable();
  public events$ = this.state$.pipe(map(state => state.events));
  public selectedEvent$ = this.state$.pipe(map(state => state.selectedEvent));
  public isLoading$ = this.state$.pipe(map(state => state.isLoading));
  public error$ = this.state$.pipe(map(state => state.error));

  public filteredEvents$ = combineLatest([
    this.events$,
    this.state$.pipe(map(state => state.filters))
  ]).pipe(
    map(([events, filters]) => this.applyFilters(events, filters))
  );

  constructor(private repository: CalendarRepositoryService) {
    this.loadEvents();
  }

  getEventsSnapshot(): CalendarEvent[] {
    return this.cloneEvents(this.stateSubject.value.events);
  }

  replaceEvents(events: CalendarEvent[]): void {
    const normalized = this.cloneEvents(events);
    this.updateState({ events: normalized });
  }

  loadEvents(): void {
    // KISS: If events are already in state, do nothing. Otherwise, load from repository once as baseline.
    if (this.stateSubject.value.events.length > 0) return;
    
    this.repository.getEvents().subscribe(events => {
      this.updateState({ events });
    });
  }

  getEventsFiltered(filters: EventFilters): Observable<CalendarEvent[]> {
    this.updateState({ filters });
    return this.filteredEvents$;
  }

  getEventById(id: string): Observable<CalendarEvent | null> {
    return this.repository.getEventById(id).pipe(
      tap(event => {
        if (event) {
          this.updateState({ selectedEvent: event });
        }
      })
    );
  }

  createEvent(eventData: Omit<CalendarEvent, 'id' | 'createdAt' | 'updatedAt'>): Observable<CalendarEvent> {
    const newEvent: CalendarEvent = {
      ...eventData,
      id: Date.now().toString(36) + Math.random().toString(36).substr(2),
      createdAt: new Date(),
      updatedAt: new Date()
    };
    const updatedEvents = [...this.stateSubject.value.events, newEvent];
    this.updateState({ events: updatedEvents });
    return of(newEvent);
  }

  updateEvent(id: string, updates: Partial<CalendarEvent>): Observable<CalendarEvent> {
    const event = this.stateSubject.value.events.find(e => e.id === id);
    if (!event) return of({} as CalendarEvent); // Should not happen in UI
    
    const updatedEvent: CalendarEvent = { ...event, ...updates, updatedAt: new Date() };
    const updatedEvents = this.stateSubject.value.events.map(e =>
      e.id === id ? updatedEvent : e
    );
    
    this.updateState({ events: updatedEvents });
    return of(updatedEvent);
  }

  deleteEvent(id: string): Observable<boolean> {
    const updatedEvents = this.stateSubject.value.events.filter(event => event.id !== id);
    this.updateState({ events: updatedEvents });
    return of(true);
  }

  reorderEvents(events: CalendarEvent[]): Observable<CalendarEvent[]> {
    const reorderedEvents = events.map((event, index) => ({ ...event, order: index }));
    this.updateState({ events: reorderedEvents });
    return of(reorderedEvents);
  }

  selectEvent(event: CalendarEvent | null): void {
    this.updateState({ selectedEvent: event });
  }

  setViewMode(mode: 'timeline' | 'calendar' | 'list'): void {
    this.updateState({ viewMode: mode });
  }

  setFilters(filters: EventFilters): void {
    this.updateState({ filters });
  }

  clearFilters(): void {
    this.updateState({ filters: {} });
  }

  getEventsByStatus(status: EventStatus): Observable<CalendarEvent[]> {
    return this.events$.pipe(
      map(events => events.filter(event => event.status === status))
    );
  }

  getEventsByType(type: EventType): Observable<CalendarEvent[]> {
    return this.events$.pipe(
      map(events => events.filter(event => event.type === type))
    );
  }

  getEventsByDateRange(startDate: Date, endDate: Date): Observable<CalendarEvent[]> {
    return this.events$.pipe(
      map(events => events.filter(event =>
        event.startDate &&
        event.endDate &&
        event.startDate >= startDate &&
        event.endDate <= endDate
      ))
    );
  }

  getProjectStats(): Observable<{
    totalEvents: number;
    completedEvents: number;
    pendingEvents: number;
    inProgressEvents: number;
    completionPercentage: number;
  }> {
    return this.events$.pipe(
      map(events => {
        const totalEvents = events.length;
        const completedEvents = events.filter(e => e.status === 'completed').length;
        const pendingEvents = events.filter(e => e.status === 'pending').length;
        const inProgressEvents = events.filter(e => e.status === 'in-progress').length;
        const completionPercentage = totalEvents > 0 ? (completedEvents / totalEvents) * 100 : 0;

        return {
          totalEvents,
          completedEvents,
          pendingEvents,
          inProgressEvents,
          completionPercentage
        };
      })
    );
  }

  private applyFilters(events: CalendarEvent[], filters: EventFilters): CalendarEvent[] {
    let filteredEvents = [...events];

    if (filters.status && filters.status.length > 0) {
      filteredEvents = filteredEvents.filter(event =>
        filters.status!.includes(event.status as EventStatus)
      );
    }

    if (filters.type && filters.type.length > 0) {
      filteredEvents = filteredEvents.filter(event =>
        filters.type!.includes(event.type as EventType)
      );
    }

    if (filters.dateRange) {
      filteredEvents = filteredEvents.filter(event =>
        event.startDate &&
        event.endDate &&
        event.startDate >= filters.dateRange!.start &&
        event.endDate <= filters.dateRange!.end
      );
    }

    return filteredEvents.sort((a, b) => (a.order || 0) - (b.order || 0));
  }

  private updateState(updates: Partial<CalendarState>): void {
    const currentState = this.stateSubject.value;
    this.stateSubject.next({ ...currentState, ...updates });
  }

  private cloneEvents(events: CalendarEvent[]): CalendarEvent[] {
    return events.map(event => ({
      ...event,
      startDate: new Date(event.startDate),
      endDate: new Date(event.endDate),
      createdAt: event.createdAt ? new Date(event.createdAt) : new Date(),
      updatedAt: event.updatedAt ? new Date(event.updatedAt) : new Date()
    }));
  }

  private setLoading(isLoading: boolean): void {
    this.updateState({ isLoading });
  }

  private setError(error: string | null): void {
    this.updateState({ error });
  }

  getCurrentState(): CalendarState {
    return this.stateSubject.value;
  }

  resetState(): void {
    this.repository.clearStorage();
    this.stateSubject.next({
      events: [],
      selectedEvent: null,
      viewMode: 'timeline',
      filters: {},
      isLoading: false,
      error: null
    });
  }

  retry(): void {
    this.setError(null);
    this.loadEvents();
  }
}
